import { ReactNode, SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";
import { TabContext, TabList } from "@mui/lab";
import { Stack, Tab, styled, tabClasses, tabsClasses, Box } from "@mui/material";

/**
 * Height of the RouteTabs tab bar in pixels.
 * Use this when calculating sticky offsets for child elements.
 * This value is explicitly set on the tab bar container to ensure consistency.
 */
export const ROUTE_TABS_HEIGHT = 52;

export interface RouteTab {
  /** Display label for the tab */
  label: string;
  /** Route segment value (e.g., "datasets", "files") */
  value: string;
  /** Whether the tab should be visible. Defaults to true. */
  visible?: boolean;
}

interface RouteTabsProps {
  /** Array of tab configurations */
  tabs: RouteTab[];
  /** Currently active tab value (derived from route) */
  currentTab: string;
  /** Optional actions to render on the right side of the tab bar */
  actions?: ReactNode;
  /** Content to render below the tabs */
  children: ReactNode;
  /** Whether to show the tab bar. Defaults to true. */
  showTabs?: boolean;
}

const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 500,
  opacity: 0.75,
  padding: 0,
  minHeight: ROUTE_TABS_HEIGHT,
  transition: "all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1)",
  [`&.${tabClasses.selected}`]: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    opacity: 1,
  },
}));

/**
 * A reusable tab navigation component that syncs with React Router.
 * Tabs navigate to relative routes when clicked.
 *
 * @example
 * ```tsx
 * const tabs = [
 *   { label: "Datasets", value: "datasets" },
 *   { label: "Files", value: "files" },
 *   { label: "Tables", value: "tables", visible: hasPermission },
 * ];
 *
 * <RouteTabs tabs={tabs} currentTab={currentTab}>
 *   {children}
 * </RouteTabs>
 * ```
 */
const RouteTabs = ({ tabs, currentTab, actions, children, showTabs = true }: RouteTabsProps) => {
  const navigate = useNavigate();

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    navigate(newValue);
  };

  const visibleTabs = tabs.filter((tab) => tab.visible !== false);

  // Ensure currentTab is valid, fallback to first visible tab
  const validTabValue = visibleTabs.some((tab) => tab.value === currentTab)
    ? currentTab
    : visibleTabs[0]?.value ?? "";

  return (
    <Stack bgcolor="common.pageBackground" sx={{ minHeight: "100%", flex: 1 }}>
      <TabContext value={validTabValue}>
        {showTabs && (
          <Stack
            direction="row"
            alignItems="stretch"
            borderBottom="1px solid"
            borderColor="divider"
            pr={3}
            sx={{
              position: "sticky",
              px: 3,
              top: 0,
              height: ROUTE_TABS_HEIGHT,
              minHeight: ROUTE_TABS_HEIGHT,
              zIndex: (theme) => theme.zIndex.appBar - 1,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "saturate(1.8) blur(20px)",
            }}
          >
            <TabList
              onChange={handleTabChange}
              sx={{
                borderBottom: "none",
                width: "100%",
                minHeight: ROUTE_TABS_HEIGHT,
                [`& .${tabsClasses.flexContainer}`]: {
                  gap: 3,
                  height: "100%",
                },
                [`& .${tabsClasses.indicator}`]: {
                  backgroundColor: "text.primary",
                },
              }}
            >
              {visibleTabs.map((tab) => (
                <StyledTab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </TabList>
            {actions && (
              <Box display="flex" alignItems="center" gap={1}>
                {actions}
              </Box>
            )}
          </Stack>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>{children}</Box>
      </TabContext>
    </Stack>
  );
};

export default RouteTabs;
